import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";

const ADMIN_TOKEN_KEY = "stockpimp_admin_token";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);   // null = checking, object = ok, false = not authed
  const [error, setError] = useState(null);

  const setToken = useCallback((t) => {
    if (t) localStorage.setItem(ADMIN_TOKEN_KEY, t);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  }, []);
  const getToken = useCallback(() => localStorage.getItem(ADMIN_TOKEN_KEY), []);

  const fetchMe = useCallback(async () => {
    const token = getToken();
    if (!token) { setAdmin(false); return; }
    try {
      const res = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdmin(res.data.admin);
    } catch {
      setToken(null);
      setAdmin(false);
    }
  }, [getToken, setToken]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (username, password) => {
    setError(null);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      setToken(res.data.access_token);
      setAdmin(res.data.admin);
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || "Admin login failed";
      setError(typeof msg === "string" ? msg : "Admin login failed");
      return { ok: false, error: typeof msg === "string" ? msg : "Admin login failed" };
    }
  };

  const logout = () => {
    setToken(null);
    setAdmin(false);
  };

  // Helper — pre-authed axios instance for all admin API calls
  const authedAxios = () => {
    const token = getToken();
    return axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  };

  return (
    <AdminAuthContext.Provider value={{ admin, error, login, logout, getToken, authedAxios, refresh: fetchMe }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}
